// src/components/partner/MyPartnerPriceListSearchPanel.tsx
//
// MyPartnerPriceListSearchPanel
// - Presentation-only search UI for MyPartnerPriceList.
// - Delegates all state ownership to the parent page.
// - Uses the shared SearchPanel container for:
//   - Search / Reset buttons (single source of truth)
//   - Optional auto-search controlled by the page via `autoSearch` prop
//   - Active filter chips (removable, with readable labels)
//
// IMPORTANT:
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel has global defaults via its constants.
//   This component must only pass through `autoSearch` if provided by the page.

import React, { useMemo } from 'react';
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';

import {
  SearchPanel,
  SearchTextField,
  type ActiveChipsConfig,
  type SearchChipSpec,
  type SearchPanelAction,
  type SetSearchValueFn,
} from '@components/common/search';

import { ProductSaleType, type GetProductsParams } from '@apis/product';
import { PRODUCT_SALE_TYPE_LABELS } from '@utils/productLabels';

type ProductSearchForm = GetProductsParams;
type Option<T> = { label: string; value: T };

export type MyPartnerPriceListSearchPanelProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;

  // Draft search state (owned by page)
  draftSearch: ProductSearchForm;
  setValue: SetSearchValueFn<ProductSearchForm>;

  // Options + selected values (owned by page)
  countryOptions: Option<number>[];
  saleTypeOptions: Option<ProductSaleType>[];
  selectedCountry: Option<number> | null;
  selectedSaleType: Option<ProductSaleType> | null;

  // Stats
  totalCount: number;

  // Actions (owned by page)
  onSearch: () => void;
  onReset: () => void;

  // Header actions (e.g. export)
  exportDisabled?: boolean;
  onExport: () => void;
  exportLoading?: boolean;

  /**
   * Auto-search configuration provided by the page.
   * If omitted, SearchPanel falls back to its global defaults.
   */
  autoSearch?: { enabled: boolean; delayMs?: number };
};

const MyPartnerPriceListSearchPanel: React.FC<MyPartnerPriceListSearchPanelProps> = ({
  open,
  onOpenChange,

  draftSearch,
  setValue,

  countryOptions,
  saleTypeOptions,
  selectedCountry,
  selectedSaleType,

  totalCount,

  onSearch,
  onReset,

  exportDisabled = false,
  onExport,
  exportLoading = false,

  autoSearch,
}) => {
  /* ------------------------------------------------------------------ */
  /* Lookup maps (O(1) chip label access)                                */
  /* ------------------------------------------------------------------ */

  const countryLabelById = useMemo(() => {
    const map = new Map<number, string>();
    countryOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [countryOptions]);

  const saleTypeLabelByValue = useMemo(() => {
    const map = new Map<ProductSaleType, string>();
    saleTypeOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [saleTypeOptions]);

  /* ------------------------------------------------------------------ */
  /* Active chips configuration                                          */
  /* ------------------------------------------------------------------ */

  const chips: SearchChipSpec<ProductSearchForm>[] = useMemo(
    () => [
      {
        key: 'productName',
        label: 'Product Name',
        getValueText: (s) => String(s.productName ?? '').trim(),
        clear: ({ setValue }) => setValue('productName', null),
      },
      {
        key: 'countryId',
        label: 'Country',
        isActive: (s) => typeof s.countryId === 'number',
        getValueText: (s) => {
          const id = s.countryId;
          if (typeof id !== 'number') return '';
          return countryLabelById.get(id) ?? String(id);
        },
        clear: ({ setValue }) => setValue('countryId', null),
      },
      {
        key: 'productSaleType',
        label: 'Sale Type',
        isActive: (s) => Boolean(s.productSaleType),
        getValueText: (s) => {
          const v = s.productSaleType as ProductSaleType | undefined;
          if (!v) return '';
          return saleTypeLabelByValue.get(v) ?? PRODUCT_SALE_TYPE_LABELS?.[v] ?? String(v);
        },
        clear: ({ setValue }) => setValue('productSaleType', null),
      },
    ],
    [countryLabelById, saleTypeLabelByValue],
  );

  const chipsConfig: ActiveChipsConfig<ProductSearchForm> = useMemo(
    () => ({
      enabled: true,
      search: draftSearch,
      setValue,
      chips,
      maxVisible: 2,
      // Keep behavior consistent with other pages:
      // chip removal should immediately run a search (manual / auto-search is optional).
      onAfterClear: onSearch,
    }),
    [chips, draftSearch, onSearch, setValue],
  );

  /* ------------------------------------------------------------------ */
  /* Panel actions                                                      */
  /* ------------------------------------------------------------------ */

  const actions: SearchPanelAction[] = useMemo(
    () => [
      {
        key: 'download',
        label: 'Download',
        startIcon: <Download />,
        variant: 'outlined',
        onClick: onExport,
        disabled: exportDisabled || exportLoading,
      },
    ],
    [exportDisabled, exportLoading, onExport],
  );

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <SearchPanel
      title="Filters"
      open={open}
      onOpenChange={onOpenChange}
      onSearch={onSearch}
      onReset={onReset}
      chipsConfig={chipsConfig}
      // Pass-through only. If undefined, SearchPanel uses global defaults.
      autoSearch={autoSearch}
      actions={actions}
      footer={
        <Box display="flex" justifyContent="flex-end">
          <Typography variant="body2" color="text.secondary">
            Total: <b>{Number(totalCount).toLocaleString()}</b>
          </Typography>
        </Box>
      }
    >
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ minWidth: 0 }}>
        <SearchTextField
          label="Product Name"
          size="small"
          value={draftSearch.productName ?? ''}
          onChangeValue={(v) => setValue('productName', v)}
          onClear={() => setValue('productName', null)}
          placeholder="Search by name"
          sx={{ minWidth: 260, flex: '2 1 260px' }}
        />

        <Autocomplete<Option<number>, false, false, false>
          disablePortal
          options={countryOptions}
          value={selectedCountry}
          onChange={(_e, v) => setValue('countryId', (v?.value ?? null) as number | null)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          renderInput={(params) => <TextField {...params} label="Country" placeholder="Select country" size="small" />}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />

        <Autocomplete<Option<ProductSaleType>, false, false, false>
          disablePortal
          options={saleTypeOptions}
          value={selectedSaleType}
          onChange={(_e, v) => setValue('productSaleType', (v?.value ?? null) as ProductSaleType | null)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          renderInput={(params) => <TextField {...params} label="Sale Type" placeholder="Select sale type" size="small" />}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />
      </Stack>
    </SearchPanel>
  );
};

export default MyPartnerPriceListSearchPanel;
